/// <reference types="vite/client" />
import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuthStore } from '../../store/authStore';
import { clearClientSessionState } from '../../lib/sessionCleanup';
import { isAuthFailure, isCsrfFailure } from '../../lib/authErrors';
import { API_BASE_URL, apiUrl } from './config';

declare module 'axios' {
  interface AxiosRequestConfig {
    skipAuthRefresh?: boolean;
  }
}

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 60000,
});

let refreshPromise: Promise<string> | null = null;
let refreshRetryAfterUntil = 0;
let lastRefreshRateLimitError: unknown = null;
const CSRF_STORAGE_KEY = 'lum-csrf-token';

const AUTH_REFRESH_EXCLUDED_PATHS = new Set([
  '/api/v1/auth/login',
  '/api/v1/auth/csrf',
  '/api/v1/auth/refresh',
  '/api/v1/auth/logout',
  '/api/v1/auth/change-password',
  '/api/v1/auth/mfa/setup',
  '/api/v1/auth/mfa/enable',
  '/api/v1/auth/mfa/disable',
  '/api/v1/auth/forgot-password',
  '/api/v1/auth/reset-password',
]);

function getPathname(url?: string): string {
  if (!url) return '';
  try {
    return new URL(url, `${API_BASE_URL}/`).pathname;
  } catch {
    return url.split('?')[0] ?? '';
  }
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function getCsrfToken(): string | null {
  // The API response is authoritative. A frontend-domain mirror cookie can be
  // stale because the actual CSRF cookie is scoped to the API subdomain.
  return getStoredCsrfToken() || getCookie('csrfToken');
}

function getStoredCsrfToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage.getItem(CSRF_STORAGE_KEY) || window.localStorage.getItem(CSRF_STORAGE_KEY);
}

function parseRetryAfterMs(value?: string | number | null): number | null {
  if (value == null) return null;
  const rawValue = String(value).trim();
  const retryAfterSeconds = Number(rawValue);
  if (Number.isFinite(retryAfterSeconds)) {
    return Math.max(0, retryAfterSeconds * 1000);
  }

  const retryAfterDate = Date.parse(rawValue);
  if (!Number.isNaN(retryAfterDate)) {
    return Math.max(0, retryAfterDate - Date.now());
  }

  return null;
}

export function getRetryAfterMs(error: unknown): number | null {
  const retryAfter = (error as { response?: { headers?: Record<string, string | number> } })?.response?.headers?.['retry-after']
    ?? (error as { response?: { headers?: Record<string, string | number> } })?.response?.headers?.['Retry-After'];

  return parseRetryAfterMs(retryAfter);
}

export function isRateLimitFailure(error: unknown): boolean {
  return (error as { response?: { status?: number } })?.response?.status === 429;
}

function restoreReadableCsrfCookie(token: string): void {
  if (typeof document === 'undefined') return;
  // Keep the frontend-domain mirror aligned with the token returned by the API.
  document.cookie = `csrfToken=${encodeURIComponent(token)}; Path=/; SameSite=Lax; Max-Age=${7 * 24 * 60 * 60}`;
}

export function rememberCsrfToken(token?: string | null): void {
  if (typeof window === 'undefined' || !token) return;
  window.sessionStorage.setItem(CSRF_STORAGE_KEY, token);
  window.localStorage.setItem(CSRF_STORAGE_KEY, token);
  restoreReadableCsrfCookie(token);
}

export function clearRememberedCsrfToken(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(CSRF_STORAGE_KEY);
  window.localStorage.removeItem(CSRF_STORAGE_KEY);
  document.cookie = 'csrfToken=; Path=/; SameSite=Lax; Max-Age=0';
}

export function shouldSkipAuthRefresh(config?: { url?: string; skipAuthRefresh?: boolean }): boolean {
  if (!config) return false;
  return Boolean(config.skipAuthRefresh) || AUTH_REFRESH_EXCLUDED_PATHS.has(getPathname(config.url));
}

export async function refreshCsrfToken(): Promise<string> {
  const { data } = await axios.post(
    apiUrl('/api/v1/auth/csrf'),
    {},
    { withCredentials: true },
  );
  const csrfToken: string = data.data.csrfToken;
  rememberCsrfToken(csrfToken);
  return csrfToken;
}

async function performTokenRefresh(): Promise<string> {
  const requestRefresh = (csrfToken: string | null) => axios.post(
    apiUrl('/api/v1/auth/refresh'),
    {},
    {
      withCredentials: true,
      headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : undefined,
    },
  );

  let csrfToken = getCsrfToken();
  if (csrfToken) restoreReadableCsrfCookie(csrfToken);

  let response;
  try {
    response = await requestRefresh(csrfToken);
  } catch (error) {
    if (!isCsrfFailure(error)) throw error;

    // Production frontend and API origins differ. Recover once when a page
    // reload has kept an older stored token than the API-domain CSRF cookie.
    csrfToken = await refreshCsrfToken();
    response = await requestRefresh(csrfToken);
  }

  const newToken: string = response.data.data.accessToken;
  rememberCsrfToken(response.data.data.csrfToken);
  useAuthStore.getState().setAccessToken(newToken);
  return newToken;
}

export function refreshAccessToken(): Promise<string> {
  if (refreshRetryAfterUntil > Date.now()) {
    return Promise.reject(lastRefreshRateLimitError);
  }

  if (!refreshPromise) {
    refreshPromise = performTokenRefresh()
      .catch((error) => {
        if (isCsrfFailure(error)) {
          clearRememberedCsrfToken();
        }
        if (isRateLimitFailure(error)) {
          const retryAfterMs = getRetryAfterMs(error) ?? 5000;
          refreshRetryAfterUntil = Date.now() + Math.min(retryAfterMs, 60 * 1000);
          lastRefreshRateLimitError = error;
          throw error;
        }
        if (isAuthFailure(error)) {
          clearClientSessionState();
        }
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

/**
 * Ensures a valid access token is available. If a refresh is already
 * in-flight, callers share the same promise to avoid duplicate calls.
 */
async function ensureAccessToken(): Promise<string | null> {
  const { accessToken, isAuthenticated } = useAuthStore.getState();
  if (accessToken) return accessToken;
  if (!isAuthenticated) return null;

  return refreshAccessToken();
}

// Request interceptor: proactively refresh token if missing, then attach headers.
api.interceptors.request.use(async (config) => {
  const token = shouldSkipAuthRefresh(config)
    ? useAuthStore.getState().accessToken
    : await ensureAccessToken();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const csrfToken = getCsrfToken();
  if (csrfToken) {
    config.headers['X-CSRF-Token'] = csrfToken;
  }
  return config;
});

// Response interceptor: fallback refresh on 401 (e.g. token expired mid-session).
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean };

    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry && !shouldSkipAuthRefresh(originalRequest)) {
      originalRequest._retry = true;

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers!.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        if (isAuthFailure(refreshError)) {
          clearClientSessionState();
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export { api };
export default api;
